import { Table } from 'antd';
import { React, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import BorderlessButton from '../../common/buttons/BorderlessButton';
import OnTraccrTextInput from '../../common/inputs/OnTraccrTextInput';

import colors from '../../constants/Colors';
import CostCodeUploadList from '../../costcodes/costcodeUploadList';
import { isNullOrUndefined, sortByNumber } from '../../helpers/helpers';
import config from '../../config';
import {
  costCodeCategoryMap,
  hasDates,
  totalCategoryValues,
} from '../projectHelpers';
import { currencyFormatter, currencyParser } from '../../helpers/inputParsers';
import VerticallyCenteredNumberInput from '../../common/inputs/VerticallyCenteredNumberInput';

import { calculateEstimatedCostFromHours } from './budgetTrackingHelpers';
import { EQUIPMENT_CATEGORY_ID, getNewCategoryAssignments } from '../../helpers/costcodeHelpers';

const title = () => 'Phases';

const getNewEstimatedCost = ({
  costcode,
  existingCategoryAssignments,
  updatedCategoryData,
  data,
}) => {
  const { dailyWage = 0, hourlyWage = 0 } = costcode;
  const {
    days: existingDays = 0,
    hours: existingHours = 0,
  } = existingCategoryAssignments[EQUIPMENT_CATEGORY_ID];
  const {
    days: updatedDays,
    hours: updatedHours,
  } = updatedCategoryData;
  const days = isNullOrUndefined(updatedDays) ? existingDays : updatedDays;
  const hours = isNullOrUndefined(updatedHours) ? existingHours : updatedHours;
  // Also recalculate if dailyWage or hourlyWage changed
  const updatedDailyWage = data.dailyWage !== undefined ? data.dailyWage : dailyWage;
  const updatedHourlyWage = data.hourlyWage !== undefined ? data.hourlyWage : hourlyWage;
  const newEstimatedCost = (
    ((days ?? 0) * updatedDailyWage)
    + ((hours ?? 0) * updatedHourlyWage)
  );

  return Number.isNaN(newEstimatedCost) ? 0 : newEstimatedCost;
};

function ProjectBudgetTrackingPhases({
  isNotDisplay,
  dataSource,
  onEditPhaseCostCodes,
  onEditPhaseDetails,
  onRemove,
  openAddDrawer,
  setSelectedCostCodes,
  setNewPhaseDescription,
  setCodeRef,
  setNewPhaseCode,
  addPhaseCode,
  newPhaseCode,
  newPhaseDescription,
  useDates,
  selectedCostCodes,
  categories,
  useBuildEstimate,
  showSingleCategory,
}) {
  const categoriesIdMap = useSelector((state) => state.costcodes.categoriesIdMap);

  // Get custom categories (those without isDefault property)
  const customCategories = Object.values(categories || {}).filter(
    (category) => !category?.isDefault,
  );

  const ourData = (isNotDisplay ? [{ id: 'add' }] : []).concat(dataSource);
  const weHaveDates = hasDates(ourData);

  const onEstimateChanged = useCallback((phase, costcode, data) => {
    if (phase.id !== 'add') {
      // Editing existing
      const updatedPhaseCostCodes = phase.costcodes.map((cc) => {
        if (cc.id === costcode.id) {
          const newCategoryAssignments = getNewCategoryAssignments({
            oldCostcode: cc,
            newData: data,
          });

          // this should only be one category
          const [
            updatedCategoryId,
            updatedCategoryData,
          ] = Object.entries(data?.categoryAssignments || {})[0] ?? [null, {}];

          const updateObj = {
            ...cc,
            ...data,
          };

          // Handle direct cost edits first - if user manually entered a cost, use it
          if ('estimatedCost' in updatedCategoryData) {
            newCategoryAssignments[updatedCategoryId].estimatedCost = (
              updatedCategoryData.estimatedCost
            );
            updateObj.categoryAssignments = newCategoryAssignments;
            return updateObj;
          }

          if ('hours' in updatedCategoryData && !!useBuildEstimate) {
            const { hourlyWage = 0 } = costcode;
            const { hours = 0 } = updatedCategoryData;
            const calculatedEstimatedCost = calculateEstimatedCostFromHours(hourlyWage, hours);
            newCategoryAssignments[updatedCategoryId].estimatedCost = calculatedEstimatedCost;
            updateObj.categoryAssignments = newCategoryAssignments;
            return updateObj;
          }
          // For equipment costcodes, always calculate combined cost from both days and hours
          if (useBuildEstimate && EQUIPMENT_CATEGORY_ID in (cc?.categoryAssignments ?? {})) {
            const newEstimatedCost = getNewEstimatedCost({
              costcode: cc,
              existingCategoryAssignments: cc?.categoryAssignments ?? {},
              updatedCategoryData,
              data,
            });
            newCategoryAssignments[EQUIPMENT_CATEGORY_ID].estimatedCost = newEstimatedCost;
          }
          updateObj.categoryAssignments = newCategoryAssignments;
          return updateObj;
        }
        return cc;
      });
      onEditPhaseCostCodes(phase.id, updatedPhaseCostCodes);
    } else {
      const updatedPhaseCostCodes = selectedCostCodes.map((cc) => {
        if (cc.id === costcode.id) {
          const newCategoryAssignments = getNewCategoryAssignments({
            oldCostcode: cc,
            newData: data,
          });

          // this should only be one category
          const [
            updatedCategoryId,
            updatedCategoryData,
          ] = Object.entries(data?.categoryAssignments || {})[0] ?? [null, {}];

          const updateObj = {
            ...cc,
            ...data,
          };

          // Handle direct cost edits first - if user manually entered a cost, use it
          if ('estimatedCost' in updatedCategoryData) {
            newCategoryAssignments[updatedCategoryId].estimatedCost = (
              updatedCategoryData.estimatedCost
            );
            updateObj.categoryAssignments = newCategoryAssignments;
            return updateObj;
          }

          if ('hours' in updatedCategoryData && !!useBuildEstimate) {
            const { hourlyWage = 0 } = costcode;
            const { hours = 0 } = updatedCategoryData;
            const calculatedEstimatedCost = calculateEstimatedCostFromHours(hourlyWage, hours);
            newCategoryAssignments[updatedCategoryId].estimatedCost = calculatedEstimatedCost;
            updateObj.categoryAssignments = newCategoryAssignments;
            return updateObj;
          }

          // For equipment costcodes, always calculate combined cost from both days and hours
          if (useBuildEstimate && EQUIPMENT_CATEGORY_ID in (cc?.categoryAssignments ?? {})) {
            const newEstimatedCost = getNewEstimatedCost({
              costcode: cc,
              existingCategoryAssignments: cc?.categoryAssignments ?? {},
              updatedCategoryData,
              data,
            });
            newCategoryAssignments[EQUIPMENT_CATEGORY_ID].estimatedCost = newEstimatedCost;
          }
          updateObj.categoryAssignments = newCategoryAssignments;
          return updateObj;
        }
        return cc;
      });
      setSelectedCostCodes(updatedPhaseCostCodes);
    }
  }, [
    selectedCostCodes,
    onEditPhaseCostCodes,
    setSelectedCostCodes,
    useBuildEstimate,
    categories,
  ]);

  const getCostCodeByIndex = useCallback(
    (index, record) => (index === 0
      ? selectedCostCodes
      : record.costcodes),
    [
      selectedCostCodes,
    ],
  );

  const getPhaseCostcodeLists = useCallback((record, index) => {
    const ourCodes = (index === 0 && isNotDisplay
      ? selectedCostCodes
      : record.costcodes
    ).sort(sortByNumber('code'));

    const categoryMap = costCodeCategoryMap(ourCodes);

    // Map through all categories (default + custom)
    return Object.entries(categoryMap).map(([categoryId, categoryCostCodes]) => {
      if (!categoryCostCodes.length) return null;

      const categoryInfo = categoriesIdMap[categoryId] || {};
      const categoryName = categoryInfo.name || '';

      return (
        <CostCodeUploadList
          key={categoryId}
          categoryId={categoryId}
          categories={categoriesIdMap}
          cost
          onEstimateChanged={
            isNotDisplay
              ? (costcode, value) => onEstimateChanged(record, costcode, value)
              : undefined
          }
          hours={categoryName !== 'Material'}
          days={categoryName === 'Equipment'}
          quantity={categoryName === 'Material'}
          units={categoryName === 'Material'}
          dates={useDates || (!isNotDisplay && weHaveDates)}
          dataSource={categoryCostCodes}
          style={{ marginTop: 15 }}
          showCostEstimates
          showSingleCategory={showSingleCategory}
        />
      );
    });
  }, [
    isNotDisplay,
    onEstimateChanged,
    useDates,
    weHaveDates,
    selectedCostCodes,
    categoriesIdMap,
    showSingleCategory,
  ]);

  const onActionClick = useCallback((record, index) => () => (
    index === 0 ? addPhaseCode() : onRemove(record)
  ), [
    addPhaseCode,
    onRemove,
  ]);

  const onClickCostcodes = useCallback((index) => () => openAddDrawer(index), [openAddDrawer]);
  const onChangeValue = useCallback((fn) => (e) => fn(e.target.value), []);

  const onEditPhaseEstimates = useCallback((id, key) => (newValue) => (
    onEditPhaseDetails(id, { [key]: newValue })
  ), [onEditPhaseDetails]);

  const getNumberInput = useCallback((value, id, key, isCurrency = false) => (
    <VerticallyCenteredNumberInput
      min={0}
      value={value}
      style={{ width: 120 }}
      onChange={onEditPhaseEstimates(id, key)}
      asFloat
      formatter={isCurrency ? currencyFormatter : undefined}
      parser={isCurrency ? currencyParser : undefined}
    />
  ), [onEditPhaseEstimates]);

  const getCols = useCallback(() => [
    {
      title: 'Phase',
      dataIndex: 'name',
      key: 'name',
      render: (text, record, index) => {
        if (index === 0 && isNotDisplay) {
          return (
            <OnTraccrTextInput
              onRef={setCodeRef}
              value={newPhaseCode}
              onChange={onChangeValue(setNewPhaseCode)}
              onPressEnter={addPhaseCode}
            />
          );
        }
        return record.name || text;
      },
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (text, record, index) => {
        if (index === 0 && isNotDisplay) {
          return (
            <OnTraccrTextInput
              value={newPhaseDescription}
              onChange={onChangeValue(setNewPhaseDescription)}
              onPressEnter={addPhaseCode}
            />
          );
        }
        return text;
      },
    },
    {
      title: 'Estimated Labour Hours',
      dataIndex: 'estimatedLabourHours',
      key: 'estimatedLabourHours',
      width: 150,
      render: (text, record, index) => {
        if (index === 0 && isNotDisplay) return '';
        if (isNotDisplay) return getNumberInput(text, record.id, 'estimatedLabourHours');
        return text;
      },
    },
    {
      title: 'Estimated Labour Cost',
      dataIndex: 'estimatedLabourCost',
      key: 'estimatedLabourCost',
      width: 150,
      render: (text, record, index) => {
        if (index === 0 && isNotDisplay) return '';
        if (isNotDisplay) return getNumberInput(text, record.id, 'estimatedLabourCost', true);
        return currencyFormatter(text);
      },
    },
    {
      title: 'Estimated Overhead Hours',
      dataIndex: 'estimatedOverheadHours',
      key: 'estimatedOverheadHours',
      width: 150,
      render: (text, record, index) => {
        if (index === 0 && isNotDisplay) return '';
        if (isNotDisplay) return getNumberInput(text, record.id, 'estimatedOverheadHours');
        return text;
      },
    },
    {
      title: 'Estimated Overhead Cost',
      dataIndex: 'estimatedOverheadCost',
      key: 'estimatedOverheadCost',
      width: 150,
      render: (text, record, index) => {
        if (index === 0 && isNotDisplay) return '';
        if (isNotDisplay) return getNumberInput(text, record.id, 'estimatedOverheadCost', true);
        return currencyFormatter(text);
      },
    },
    {
      title: 'Estimated Material Cost',
      dataIndex: 'estimatedMaterialCost',
      key: 'estimatedMaterialCost',
      width: 150,
      render: (text, record, index) => {
        if (index === 0 && isNotDisplay) return '';
        if (isNotDisplay) return getNumberInput(text, record.id, 'estimatedMaterialCost', true);
        return currencyFormatter(text);
      },
    },
    {
      title: 'Estimated Equipment Hours',
      dataIndex: 'estimatedEquipmentHours',
      key: 'estimatedEquipmentHours',
      width: 150,
      render: (text, record, index) => {
        if (index === 0 && isNotDisplay) return '';
        if (isNotDisplay) return getNumberInput(text, record.id, 'estimatedEquipmentHours');
        return text;
      },
    },
    {
      title: 'Estimated Equipment Cost',
      dataIndex: 'estimatedEquipmentCost',
      key: 'estimatedEquipmentCost',
      width: 150,
      render: (text, record, index) => {
        if (index === 0 && isNotDisplay) return '';
        if (isNotDisplay) return getNumberInput(text, record.id, 'estimatedEquipmentCost', true);
        return currencyFormatter(text);
      },
    },
    ...customCategories.map((customCategory) => ({
      title: `Estimated ${customCategory.name} Cost`,
      dataIndex: `phaseEstimate.${customCategory.id}`,
      key: `phaseEstimate.${customCategory.id}`,
      width: 150,
      render: (text, record, index) => {
        if (index === 0 && isNotDisplay) return '';
        if (isNotDisplay) return getNumberInput(text, record.id, `phaseEstimate.${customCategory.id}`, true);
        return currencyFormatter(text);
      },
    })),
    {
      title: (isNotDisplay ? 'Cost Codes' : ''),
      dataIndex: 'costcodes',
      key: 'costcodes',
      align: 'center',
      render: (text, record, index) => {
        if (!isNotDisplay) return null;
        return (
          <BorderlessButton
            title={getCostCodeByIndex(index, record).length === 0 ? 'Add' : 'Edit'}
            icon={null}
            color={colors.ONTRACCR_BLACK}
            onClick={onClickCostcodes(index)}
            style={{
              paddingRight: 8,
              paddingLeft: 0,
            }}
          />
        );
      },
    },
    {
      dataIndex: 'action',
      key: 'action',
      align: 'center',
      render: (_, record, index) => {
        if (!isNotDisplay) return null;
        return (
          <BorderlessButton
            title={index === 0 ? 'Save' : ''}
            icon={index === 0 ? null : 'delete'}
            color={index === 0 ? colors.ONTRACCR_BLACK : colors.ONTRACCR_RED}
            onClick={onActionClick(record, index)}
            style={{
              paddingRight: 8,
              paddingLeft: 0,
              visibility: index === 0 && !(newPhaseCode && newPhaseDescription) ? 'hidden' : 'visible',
            }}
          />
        );
      },
    },
  ], [
    isNotDisplay,
    setCodeRef,
    newPhaseCode,
    setNewPhaseCode,
    addPhaseCode,
    newPhaseDescription,
    setNewPhaseDescription,
    getCostCodeByIndex,
    openAddDrawer,
    onRemove,
    getNumberInput,
    customCategories,
  ]);

  return (
    <Table
      title={title}
      expandable={{
        rowExpandable: (record) => (
          record.id === 'add' && selectedCostCodes.length > 0)
          || (record.costcodes && record.costcodes.length > 0),
        expandedRowRender: getPhaseCostcodeLists,
        // defaultExpandAllRows: true,
      }}
      size="small"
      columns={getCols()}
      scroll={{ x: 'max-content' }}
      dataSource={ourData.map((item) => {
        const baseData = {
          ...item,
          key: item.id,
          estimatedLabourHours: item?.estimatedLabourHours ?? totalCategoryValues(item.costcodes, categories, 'Labor', 'hours'),
          estimatedLabourCost: item?.estimatedLabourCost ?? totalCategoryValues(item.costcodes, categories, 'Labor', 'estimatedCost'),
          estimatedOverheadHours: item?.estimatedOverheadHours ?? totalCategoryValues(item.costcodes, categories, 'Overhead', 'hours'),
          estimatedOverheadCost: item?.estimatedOverheadCost ?? totalCategoryValues(item.costcodes, categories, 'Overhead', 'estimatedCost'),
          estimatedMaterialCost: item?.estimatedMaterialCost ?? totalCategoryValues(item.costcodes, categories, 'Material', 'estimatedCost'),
          estimatedEquipmentCost: item?.estimatedEquipmentCost ?? totalCategoryValues(item.costcodes, categories, 'Equipment', 'estimatedCost', true),
          estimatedEquipmentHours: item?.estimatedEquipmentHours ?? totalCategoryValues(item.costcodes, categories, 'Equipment', 'hours'),
        };

        // Add custom category estimates
        const customCategoryData = {};
        customCategories.forEach((customCategory) => {
          const key = `phaseEstimate.${customCategory.id}`;
          const phaseEstimate = item?.[key];
          customCategoryData[key] = phaseEstimate ?? totalCategoryValues(
            item.costcodes,
            categories,
            customCategory.name,
            'estimatedCost',
            true,
          );
        });

        return { ...baseData, ...customCategoryData };
      })}
      pagination={false}
      style={{ marginBottom: config.showEquipment ? 50 : 100 }}
    />
  );
}

/* eslint-disable react/forbid-prop-types */
ProjectBudgetTrackingPhases.propTypes = {
  dataSource: PropTypes.array.isRequired,
  isNotDisplay: PropTypes.bool.isRequired,
  onEditPhaseCostCodes: PropTypes.func.isRequired,
  onEditPhaseDetails: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  openAddDrawer: PropTypes.func.isRequired,
  setSelectedCostCodes: PropTypes.func.isRequired,
  setNewPhaseDescription: PropTypes.func.isRequired,
  setCodeRef: PropTypes.func.isRequired,
  setNewPhaseCode: PropTypes.func.isRequired,
  addPhaseCode: PropTypes.func.isRequired,
  newPhaseCode: PropTypes.string,
  newPhaseDescription: PropTypes.string,
  useDates: PropTypes.bool,
  selectedCostCodes: PropTypes.array.isRequired,
  categories: PropTypes.object.isRequired,
  useBuildEstimate: PropTypes.bool.isRequired,
  showSingleCategory: PropTypes.bool,
};

ProjectBudgetTrackingPhases.defaultProps = {
  useDates: false,
  newPhaseDescription: null,
  newPhaseCode: null,
  showSingleCategory: false,
};

export default ProjectBudgetTrackingPhases;
